import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import './GuidelineView.css';
import HighlightedTextarea from '../components/HighlightedTextarea';
import PromptStats from '../components/PromptStats';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function GuidelineView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [guideline, setGuideline] = useState(null);
  const [llmGeneratedGuideline, setLlmGeneratedGuideline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingLlm, setLoadingLlm] = useState(false);
  const [error, setError] = useState(null);
  const [showStructuredDialog, setShowStructuredDialog] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [processingStructured, setProcessingStructured] = useState(false);
  const [processMessage, setProcessMessage] = useState(null);
  const [showDefaultPrompt, setShowDefaultPrompt] = useState(false);
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);
  const [llmProvider, setLlmProvider] = useState('gemini');
  const [showDetails, setShowDetails] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const abortControllerRef = useRef(null);

  const fetchGuideline = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const decodedId = decodeURIComponent(id);
      const url = `${API_BASE_URL}/processed-guidelines/${encodeURIComponent(decodedId)}`;
      console.log('Fetching guideline from:', url);
      console.log('Guideline ID:', decodedId);
      
      const response = await axios.get(url, {
        timeout: 30000, // 30 second timeout for large responses
        headers: {
          'Accept': 'application/json',
        },
        validateStatus: function (status) {
          return status < 500; // Don't throw for 4xx errors, let us handle them
        }
      });
      
      if (response.status >= 400) {
        throw new Error(response.data?.detail || `HTTP ${response.status}`);
      }
      
      console.log('Guideline fetched successfully:', response.data?.guideline_id);
      setGuideline(response.data);
      
      // Fetch LLM generated guideline if it exists
      if (response.data?.guideline_id) {
        await fetchLlmGeneratedGuideline(response.data.guideline_id);
      }
    } catch (err) {
      console.error('Error fetching guideline:', err);
      console.error('Error details:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      let errorMessage = 'Failed to load guideline';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. The guideline data may be too large. Please try again or contact support.';
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and ensure the backend server is running.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Guideline not found';
      } else if (err.response?.status >= 500) {
        errorMessage = `Server error (${err.response.status}). The guideline data may be corrupted or too large.`;
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchGuideline();
    }
  }, [id, fetchGuideline]);

  const fetchLlmGeneratedGuideline = async (guidelineId) => {
    try {
      setLoadingLlm(true);
      const url = `${API_BASE_URL}/llm-generated-guidelines/${encodeURIComponent(guidelineId)}`;
      console.log('Fetching LLM generated guideline from:', url);
      
      const response = await axios.get(url, {
        timeout: 30000,
        headers: {
          'Accept': 'application/json',
        },
        validateStatus: function (status) {
          return status < 500; // Don't throw for 4xx errors
        }
      });
      
      if (response.status === 404) {
        // LLM generated guideline doesn't exist, which is fine
        console.log('No LLM generated guideline found for this guideline_id');
        setLlmGeneratedGuideline(null);
      } else if (response.status >= 400) {
        console.warn('Error fetching LLM generated guideline:', response.data?.detail);
        setLlmGeneratedGuideline(null);
      } else {
        console.log('LLM generated guideline fetched successfully');
        setLlmGeneratedGuideline(response.data);
      }
    } catch (err) {
      console.warn('Error fetching LLM generated guideline:', err);
      // Don't set error state, just log it - it's optional data
      setLlmGeneratedGuideline(null);
    } finally {
      setLoadingLlm(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatArray = (arr) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return null;
    return arr;
  };

  const getMarkdownContent = (content) => {
    if (!content) return null;
    
    try {
      // If content is an object, try to extract markdown
      if (typeof content === 'object') {
        // Check if there's a markdown field
        if (content.markdown) {
          return content.markdown;
        }
        // Otherwise, stringify and format as code
        return `\`\`\`json\n${JSON.stringify(content, null, 2)}\n\`\`\``;
      }
      
      // If it's already a string, return it
      if (typeof content === 'string') {
        return content;
      }
    } catch (err) {
      console.error('Error processing markdown content:', err);
      return null;
    }
    
    return null;
  };

  const formatReferencedGuidelines = (referencedData) => {
    if (!referencedData) return null;
    
    try {
      // If it's already a string, return it
      if (typeof referencedData === 'string') {
        return referencedData;
      }
      
      // If it's an object, format it nicely
      if (typeof referencedData === 'object') {
        // Check if it's a nested structure with guideline IDs as keys
        if (Object.keys(referencedData).length > 0) {
          let formatted = '# Referenced Guidelines\n\n';
          
          Object.entries(referencedData).forEach(([guidelineId, refData]) => {
            formatted += `## ${guidelineId}\n\n`;
            
            if (refData && typeof refData === 'object') {
              if (refData.insurer) {
                formatted += `**Insurer:** ${refData.insurer}\n\n`;
              }
              if (refData.ref_cpb || refData.cpb) {
                formatted += `**CPB:** ${refData.ref_cpb || refData.cpb}\n\n`;
              }
              if (refData.ref_content || refData.original_content) {
                const refContent = refData.ref_content || refData.original_content;
                if (typeof refContent === 'object' && refContent.markdown) {
                  formatted += `**Content:**\n\n${refContent.markdown}\n\n`;
                } else if (typeof refContent === 'string') {
                  formatted += `**Content:**\n\n${refContent}\n\n`;
                } else {
                  formatted += `**Content:**\n\n\`\`\`json\n${JSON.stringify(refContent, null, 2)}\n\`\`\`\n\n`;
                }
              }
            }
            
            formatted += '---\n\n';
          });
          
          return formatted;
        }
        
        // If it has a markdown field, use that
        if (referencedData.markdown) {
          return referencedData.markdown;
        }
        
        // Otherwise, format as JSON
        return `\`\`\`json\n${JSON.stringify(referencedData, null, 2)}\n\`\`\``;
      }
    } catch (err) {
      console.error('Error formatting referenced guidelines:', err);
      return null;
    }
    
    return null;
  };

  const availableVariables = [
    { name: 'guideline_id', description: 'The unique identifier for the guideline', type: 'string' },
    { name: 'cpb', description: 'Clinical Policy Bulletin title', type: 'string' },
    { name: 'procedures', description: 'JSON object mapping procedure codes to descriptions', type: 'object' },
    { name: 'original_content', description: 'JSON object with original guideline content', type: 'object' },
    { name: 'referenced_guidelines', description: 'JSON object containing referenced guideline data', type: 'object' }
  ];

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Recursively render JSON in a human-readable format
  const renderFormattedJson = (data, level = 0) => {
    if (data === null || data === undefined) {
      return <span className="json-null">null</span>;
    }

    if (typeof data === 'boolean') {
      return <span className={`json-boolean ${data ? 'json-true' : 'json-false'}`}>{data.toString()}</span>;
    }

    if (typeof data === 'number') {
      return <span className="json-number">{data}</span>;
    }

    if (typeof data === 'string') {
      // Check if it's a URL
      if (data.startsWith('http://') || data.startsWith('https://')) {
        return (
          <a href={data} target="_blank" rel="noopener noreferrer" className="json-link">
            {data}
          </a>
        );
      }
      return <span className="json-string">{data}</span>;
    }

    if (Array.isArray(data)) {
      if (data.length === 0) {
        return <span className="json-empty">[ ]</span>;
      }
      return (
        <div className="json-array">
          {data.map((item, idx) => (
            <div key={idx} className="json-array-item">
              <span className="json-array-index">{idx + 1}.</span>
              <div className="json-array-value">{renderFormattedJson(item, level + 1)}</div>
            </div>
          ))}
        </div>
      );
    }

    if (typeof data === 'object') {
      const entries = Object.entries(data);
      if (entries.length === 0) {
        return <span className="json-empty">{ }</span>;
      }
      return (
        <div className={`json-object ${level === 0 ? 'json-root' : ''}`}>
          {entries.map(([key, value], idx) => (
            <div key={idx} className="json-field">
              <div className="json-key">{formatKeyName(key)}</div>
              <div className="json-value">{renderFormattedJson(value, level + 1)}</div>
            </div>
          ))}
        </div>
      );
    }

    return <span>{String(data)}</span>;
  };

  // Format key names to be more readable
  const formatKeyName = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const handleCancelStructuredGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setCancelling(true);
    }
  };

  const handleGenerateStructuredGuidelines = async () => {
    if (!guideline || !guideline.guideline_id) {
      return;
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      setProcessingStructured(true);
      setCancelling(false);
      setProcessMessage(null);
      
      const response = await axios.post(
        `${API_BASE_URL}/llm-generated-guidelines/process-guideline/${encodeURIComponent(guideline.guideline_id)}`,
        {
          prompt: customPrompt || null,
          llm_provider: llmProvider,
          llm_model: null,
          llm_version: null
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 1800000, // 30 minute timeout (Gemini can take 4+ minutes for large documents)
          signal: abortControllerRef.current.signal
        }
      );

      console.log('Structured guidelines generated:', response.data);
      
      setProcessMessage({ 
        type: 'success', 
        text: `Structured guidelines generated successfully! Model: ${response.data.llm_model || 'N/A'}` 
      });
      
      setShowStructuredDialog(false);
      setCustomPrompt('');
      setLlmProvider('openai'); // Reset to default
      
      // Refresh LLM generated guideline
      await fetchLlmGeneratedGuideline(guideline.guideline_id);
      
      setTimeout(() => setProcessMessage(null), 5000);
    } catch (err) {
      // Check if this was a cancellation
      if (axios.isCancel(err) || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        setProcessMessage({ 
          type: 'warning', 
          text: 'Operation cancelled by user.' 
        });
        setTimeout(() => setProcessMessage(null), 3000);
        return;
      }

      console.error('Error generating structured guidelines:', err);
      let errorMessage = 'Failed to generate structured guidelines';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. The LLM processing is taking longer than expected.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setProcessMessage({ 
        type: 'error', 
        text: errorMessage
      });
    } finally {
      setProcessingStructured(false);
      setCancelling(false);
      abortControllerRef.current = null;
    }
  };

  if (loading) {
    return (
      <div className="guideline-view-loading">
        <div className="spinner"></div>
        <p>Loading guideline...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="guideline-view-error">
        <span className="error-icon">❌</span>
        <span>{error}</span>
        <button onClick={() => navigate('/guidelines')} className="back-button">
          Back to Guidelines
        </button>
      </div>
    );
  }

  if (!guideline) {
    return (
      <div className="guideline-view-error">
        <span>Guideline not found</span>
        <button onClick={() => navigate('/guidelines')} className="back-button">
          Back to Guidelines
        </button>
      </div>
    );
  }

  const originalContentMarkdown = getMarkdownContent(guideline.original_content);
  // Handle both 'referenced_guidelines' (from model) and 'references' (from schema)
  const referencedGuidelinesData = guideline.referenced_guidelines || guideline.references;
  const referencedGuidelinesMarkdown = formatReferencedGuidelines(referencedGuidelinesData);

  return (
    <div className="guideline-view-page">
      <div className="guideline-view-header">
        <div className="header-top">
          <button onClick={() => navigate('/guidelines')} className="back-button">
            ← Back to Guidelines
          </button>
          <h1>Guideline Details</h1>
          <button
            onClick={() => setShowStructuredDialog(true)}
            className="generate-structured-button"
            disabled={processingStructured}
          >
            {processingStructured ? 'Processing...' : 'Generate Structured Guidelines'}
          </button>
        </div>

        {/* Collapsible Details Section */}
        <div className="details-collapsible">
          <button 
            className="details-toggle-button"
            onClick={() => setShowDetails(!showDetails)}
          >
            <div className="details-toggle-left">
              <span className="toggle-icon">{showDetails ? '−' : '+'}</span>
              <span className="details-toggle-title">Metadata & Procedures</span>
            </div>
            <div className="details-toggle-right">
              <div className="details-preview-badges">
                <span className="preview-badge preview-badge-id">{guideline.guideline_id}</span>
                {guideline.insurer && <span className="preview-badge preview-badge-insurer">{guideline.insurer}</span>}
                {guideline.source && <span className="preview-badge preview-badge-source">{guideline.source}</span>}
              </div>
            </div>
          </button>
          
          <div className={`header-fields-content ${showDetails ? 'expanded' : ''}`}>
            <div className="header-fields">
              <div className="header-field">
                <label className="header-field-label">Guideline ID</label>
                <div className="header-field-value header-field-value-id">{guideline.guideline_id || 'N/A'}</div>
              </div>

              <div className="header-field">
                <label className="header-field-label">CPB</label>
                <div className="header-field-value header-field-value-title">{guideline.cpb || 'N/A'}</div>
              </div>

              <div className="header-field">
                <label className="header-field-label">Insurer</label>
                <div className="header-field-value">
                  {guideline.insurer ? (
                    <span className="value-badge insurer-badge">{guideline.insurer}</span>
                  ) : (
                    'N/A'
                  )}
                </div>
              </div>

              <div className="header-field">
                <label className="header-field-label">Source</label>
                <div className="header-field-value">
                  {guideline.source ? (
                    <span className="value-badge source-badge">{guideline.source}</span>
                  ) : (
                    'N/A'
                  )}
                </div>
              </div>

              <div className="header-field">
                <label className="header-field-label">State(s)</label>
                <div className="header-field-value">
                  {guideline.state ? (
                    <div className="code-badges">
                      {guideline.state.split(';').map((s, idx) => (
                        <span key={idx} className="value-badge source-badge">{s.trim()}</span>
                      ))}
                    </div>
                  ) : (
                    'N/A'
                  )}
                </div>
              </div>

              {guideline.procedures && Object.keys(guideline.procedures).length > 0 && (
                <div className="header-field header-field-wide">
                  <label className="header-field-label">Procedures (CPT Codes)</label>
                  <div className="header-field-value">
                    <div className="scrollable-codes-box">
                      <table className="procedures-table">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(guideline.procedures).map(([code, description], idx) => (
                            <tr key={idx}>
                              <td><span className="code-badge cpt-badge">{code}</span></td>
                              <td className="procedure-description">{description || 'No description available'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {formatArray(guideline.hcpcs_codes) && (
                <div className="header-field">
                  <label className="header-field-label">HCPCS Codes</label>
                  <div className="header-field-value">
                    <div className="scrollable-codes-box scrollable-codes-box-small">
                      <div className="code-badges">
                        {guideline.hcpcs_codes.map((code, idx) => (
                          <span key={idx} className="code-badge hcpcs-badge">{code}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="header-field">
                <label className="header-field-label">Link</label>
                <div className="header-field-value">
                  {guideline.link ? (
                    <a href={guideline.link} target="_blank" rel="noopener noreferrer" className="external-link">
                      {guideline.link}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </div>
              </div>

              <div className="header-field">
                <label className="header-field-label">Effective Date</label>
                <div className="header-field-value">
                  {guideline.effective_date ? (
                    <span className="date-badge">{guideline.effective_date}</span>
                  ) : (
                    'N/A'
                  )}
                </div>
              </div>

              <div className="header-field">
                <label className="header-field-label">Created At</label>
                <div className="header-field-value">
                  {guideline.created_at ? (
                    <span className="date-badge">{formatDate(guideline.created_at)}</span>
                  ) : (
                    'N/A'
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="guideline-view-content">
        {/* Left side: Content */}
        <div className="guideline-content-box">
          <h2 className="content-box-title">Content</h2>
          {originalContentMarkdown && (
            <div className="field-item field-item-markdown">
              <label className="field-label">Original Content</label>
              <div className="field-value markdown-content">
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm]}
                >
                  {originalContentMarkdown}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {referencedGuidelinesMarkdown && (
            <div className="field-item field-item-markdown">
              <label className="field-label">Referenced Guidelines</label>
              <div className="field-value markdown-content">
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm]}
                >
                  {referencedGuidelinesMarkdown}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {/* Right side: LLM Generated Parsed JSON */}
        <div className="guideline-llm-box">
          <h2 className="content-box-title">LLM Generated Output</h2>
          {loadingLlm ? (
            <div className="llm-loading">
              <div className="spinner-small"></div>
              <p>Loading LLM output...</p>
            </div>
          ) : llmGeneratedGuideline && llmGeneratedGuideline.parsed_json ? (
            <div className="llm-content">
              <div className="llm-metadata">
                {llmGeneratedGuideline.llm_model && (
                  <div className="llm-meta-item">
                    <span className="llm-meta-label">Model:</span>
                    <span className="llm-meta-value">{llmGeneratedGuideline.llm_model}</span>
                  </div>
                )}
                {llmGeneratedGuideline.llm_version && (
                  <div className="llm-meta-item">
                    <span className="llm-meta-label">Version:</span>
                    <span className="llm-meta-value">{llmGeneratedGuideline.llm_version}</span>
                  </div>
                )}
                {llmGeneratedGuideline.created_at && (
                  <div className="llm-meta-item">
                    <span className="llm-meta-label">Generated:</span>
                    <span className="llm-meta-value">{formatDate(llmGeneratedGuideline.created_at)}</span>
                  </div>
                )}
              </div>

              {/* Raw JSON Dropdown */}
              <div className="raw-json-section">
                <div className="raw-json-header">
                  <button 
                    className="raw-json-toggle"
                    onClick={() => setShowRawJson(!showRawJson)}
                  >
                    <span className="toggle-icon">{showRawJson ? '▼' : '▶'}</span>
                    Raw JSON Output
                  </button>
                  <button 
                    className={`copy-button ${copySuccess ? 'copy-success' : ''}`}
                    onClick={() => copyToClipboard(JSON.stringify(llmGeneratedGuideline.parsed_json, null, 2))}
                  >
                    {copySuccess ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
                {showRawJson && (
                  <div className="parsed-json-content">
                    <pre>{JSON.stringify(llmGeneratedGuideline.parsed_json, null, 2)}</pre>
                  </div>
                )}
              </div>

              {/* Formatted JSON Display */}
              <div className="formatted-json-section">
                <h3 className="formatted-json-title">Structured Output</h3>
                <div className="formatted-json-content">
                  {renderFormattedJson(llmGeneratedGuideline.parsed_json)}
                </div>
              </div>
            </div>
          ) : (
            <div className="llm-empty">
              <p>No LLM generated output available for this guideline.</p>
            </div>
          )}
        </div>
      </div>

      {/* Structured Guidelines Dialog */}
      {showStructuredDialog && (
        <div className="dialog-overlay" onClick={() => !processingStructured && setShowStructuredDialog(false)}>
          <div className="prompt-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <h3>Generate Structured Guidelines</h3>
            <p className="dialog-description">
              Generate structured JSON output from this guideline using LLM Layer 2 pipeline.
              Use the variables below in your custom prompt, or leave empty to use the default prompt.
            </p>
            
            {/* Default Prompt Dropdown - Above custom prompt */}
            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={async () => {
                  if (!showDefaultPrompt && !defaultPrompt) {
                    setLoadingDefaultPrompt(true);
                    try {
                      const response = await axios.get(`${API_BASE_URL}/llm-generated-guidelines/default-prompt`);
                      setDefaultPrompt(response.data);
                    } catch (err) {
                      console.error('Error loading default prompt:', err);
                    } finally {
                      setLoadingDefaultPrompt(false);
                    }
                  }
                  setShowDefaultPrompt(!showDefaultPrompt);
                }}
                className="default-prompt-toggle"
                style={{ 
                  padding: '0.5rem 1rem', 
                  background: '#f0f0f0', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                {showDefaultPrompt ? '▼' : '▶'} {loadingDefaultPrompt ? 'Loading...' : 'Show Default Prompt'}
              </button>
              {showDefaultPrompt && defaultPrompt && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '1rem',
                  background: '#f9f9f9',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '400px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.85rem',
                  fontFamily: 'monospace'
                }}>
                  {defaultPrompt}
                </div>
              )}
              {showDefaultPrompt && defaultPrompt && (
                <PromptStats
                  text={defaultPrompt}
                  knownVariables={['guideline_id', 'insurer', 'cpb', 'source', 'cpt_codes', 'hcpcs_codes', 'procedures', 'original_content', 'referenced_guidelines', 'ref_content', 'ref_procedures', 'link', 'effective_date', 'created_at']}
                  label="Default Prompt"
                  estimateEndpoint="/llm-generated-guidelines/estimate-prompt-tokens"
                  entityId={decodeURIComponent(id)}
                />
              )}
            </div>

            {/* LLM Provider Selection */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="llm-provider" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                LLM Provider:
              </label>
              <select
                id="llm-provider"
                value={llmProvider}
                onChange={(e) => setLlmProvider(e.target.value)}
                disabled={processingStructured}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  background: processingStructured ? '#f5f5f5' : '#fff',
                  cursor: processingStructured ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="openai">OpenAI GPT-5</option>
                <option value="gemini">Google Gemini 3.0</option>
              </select>
            </div>
            
            <div className="prompt-input-section" style={{ marginBottom: '1rem' }}>
              <label htmlFor="structured-prompt">Custom Prompt (optional):</label>
              <HighlightedTextarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Enter custom prompt template here, or leave empty to use default prompt..."
                rows={8}
                disabled={processingStructured}
                variables={availableVariables.map(v => v.name)}
              />
              {customPrompt && (
                <PromptStats
                  text={customPrompt}
                  knownVariables={['guideline_id', 'insurer', 'cpb', 'source', 'cpt_codes', 'hcpcs_codes', 'procedures', 'original_content', 'referenced_guidelines', 'ref_content', 'ref_procedures', 'link', 'effective_date', 'created_at']}
                  label="Custom Prompt"
                  estimateEndpoint="/llm-generated-guidelines/estimate-prompt-tokens"
                  entityId={decodeURIComponent(id)}
                />
              )}
            </div>

            <div className="variables-section">
              <h4>Available Variables (from processed_guidelines table):</h4>
              <div className="variables-grid">
                {availableVariables.map((variable, idx) => (
                  <div key={idx} className="variable-item">
                    <code className="variable-name">{'{' + variable.name + '}'}</code>
                    <span className="variable-type">{variable.type}</span>
                    <span className="variable-description">{variable.description}</span>
                  </div>
                ))}
              </div>
            </div>

            {processMessage && (
              <div className={`process-message ${processMessage.type}`}>
                <span className="message-icon">
                  {processMessage.type === 'success' ? '✅' : processMessage.type === 'warning' ? '⚠️' : '❌'}
                </span>
                <span>{processMessage.text}</span>
              </div>
            )}

            {/* Progress Indicator */}
            {processingStructured && (
              <div className="progress-container">
                <div className="progress-header">
                  <span className={`progress-text ${cancelling ? 'cancelling-text' : ''}`}>
                    {cancelling 
                      ? '⚠️ Cancelling...' 
                      : '🔄 Generating structured guidelines with LLM...'}
                  </span>
                </div>
                <div className={`progress-bar-wrapper ${cancelling ? 'cancelling' : ''}`}>
                  <div 
                    className={`progress-bar-fill ${cancelling ? 'cancelling' : ''}`}
                    style={{ width: '100%', animation: 'progress-pulse 1.5s ease-in-out infinite' }}
                  ></div>
                </div>
              </div>
            )}

            <div className="dialog-buttons">
              {processingStructured ? (
                <button
                  onClick={handleCancelStructuredGeneration}
                  className={`cancel-processing-button ${cancelling ? 'cancelling' : ''}`}
                  disabled={cancelling}
                >
                  {cancelling ? '⏳ Cancelling...' : '⏹ Cancel Operation'}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setShowStructuredDialog(false);
                      setCustomPrompt('');
                      setProcessMessage(null);
                      setLlmProvider('openai');
                    }}
                    className="dialog-cancel-button"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleGenerateStructuredGuidelines}
                    className="dialog-confirm-button"
                  >
                    Generate Structured Guidelines
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuidelineView;
